import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CategoryHierarchy = ({ existingCategories, selectedParent, newCategoryName }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Build tree structure from flat array
  const buildTree = (categories) => {
    const tree = [];
    const categoryMap = {};

    // Create map of all categories
    categories.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });

    // Build parent-child relationships
    categories.forEach(cat => {
      if (cat.parent && categoryMap[cat.parent]) {
        categoryMap[cat.parent].children.push(categoryMap[cat.id]);
      } else if (!cat.parent) {
        tree.push(categoryMap[cat.id]);
      }
    });

    return tree;
  };

  const toggleExpanded = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const getNewCategoryPlacement = () => {
    if (!selectedParent) {
      return { level: 0, parent: null };
    }
    
    const parent = existingCategories.find(cat => cat.id === selectedParent);
    return {
      level: parent ? parent.level + 1 : 0,
      parent: parent?.name || 'Unknown'
    };
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedParent === node.id;
    const indentation = level * 20;

    return (
      <div key={node.id}>
        <div 
          className={`flex items-center py-2 px-3 rounded-md cursor-pointer transition-colors ${
            isSelected 
              ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted/50'
          }`}
          style={{ marginLeft: `${indentation}px` }}
          onClick={() => hasChildren && toggleExpanded(node.id)}
        >
          <div className="flex items-center space-x-2 flex-1">
            {hasChildren ? (
              <button
                className="p-1 hover:bg-muted/50 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(node.id);
                }}
              >
                <Icon 
                  name={isExpanded ? "ChevronDown" : "ChevronRight"} 
                  size={14} 
                  className="text-muted-foreground"
                />
              </button>
            ) : (
              <div className="w-6" />
            )}
            
            <Icon 
              name={hasChildren ? "Folder" : "Tag"} 
              size={16} 
              className={isSelected ? "text-primary" : "text-muted-foreground"}
            />
            
            <span className={`text-sm ${isSelected ? 'text-primary font-medium' : 'text-foreground'}`}>
              {node.name}
            </span>
            
            {isSelected && (
              <div className="ml-auto">
                <Icon name="ArrowDown" size={14} className="text-primary" />
              </div>
            )}
          </div>
        </div>

        {/* Show where new category will be placed */}
        {isSelected && newCategoryName && (
          <div 
            className="flex items-center py-2 px-3 bg-success/10 border border-success/20 rounded-md ml-2 mt-1"
            style={{ marginLeft: `${indentation + 20}px` }}
          >
            <div className="w-6" />
            <Icon name="Plus" size={14} className="text-success mr-2" />
            <span className="text-sm text-success font-medium">{newCategoryName}</span>
            <span className="text-xs text-success/70 ml-2">(new)</span>
          </div>
        )}

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const tree = buildTree(existingCategories);
  const placement = getNewCategoryPlacement();

  return (
    <div className="bg-white rounded-lg border border-border p-6">
      <div className="flex items-center mb-4">
        <Icon name="Sitemap" size={20} className="text-primary mr-2" />
        <h3 className="text-lg font-semibold text-foreground">Category Hierarchy</h3>
      </div>

      {/* Placement Info */}
      <div className="mb-4 p-3 bg-muted/30 rounded-md">
        <div className="flex items-center mb-2">
          <Icon name="Info" size={16} className="text-primary mr-2" />
          <span className="text-sm font-medium text-foreground">Placement</span>
        </div>
        
        {newCategoryName ? (
          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>Category:</strong> {newCategoryName}</p>
            <p><strong>Level:</strong> {placement.level + 1} {placement.level === 0 ? '(Root)' : '(Sub-category)'}</p>
            {placement.parent && <p><strong>Parent:</strong> {placement.parent}</p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Enter a category name to see placement preview
          </p>
        )}
      </div>

      {/* Tree View */}
      <div className="max-h-64 overflow-y-auto border border-border rounded-md p-3 bg-muted/10">
        {tree.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Icon name="Folder" size={32} className="mx-auto mb-2" />
            <p className="text-sm">No categories yet</p>
          </div>
        ) : (
          <div className="space-y-1">
            {tree.map(node => renderTreeNode(node))}
            
            {/* Show new category at root level if no parent selected */}
            {!selectedParent && newCategoryName && (
              <div className="flex items-center py-2 px-3 bg-success/10 border border-success/20 rounded-md mt-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Plus" size={14} className="text-success" />
                  <Icon name="Tag" size={16} className="text-success" />
                  <span className="text-sm text-success font-medium">{newCategoryName}</span>
                  <span className="text-xs text-success/70">(new root category)</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-4 text-xs text-muted-foreground space-y-1">
        <p>• Root categories appear at the top level</p>
        <p>• Sub-categories are nested under their parents</p>
        <p>• Maximum depth: 4 levels recommended</p>
        {selectedParent && (
          <p className="text-primary">• Your new category will be placed under the selected parent</p>
        )}
      </div>
    </div>
  );
};

export default CategoryHierarchy;